import React, { useState } from 'react';
import { keccak256 } from 'ethers';
import { AppButton } from '../ui/AppButton';

const readFileHash = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  return keccak256(new Uint8Array(arrayBuffer));
};

/**
 * Proof-of-Existence notarization and verification UI.
 */
export const PoEPanel = ({ onNotarizeHash, onVerifyHash, notarizing }) => {
  const [activeTab, setActiveTab] = useState('notarize');
  const [hashInput, setHashInput] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);

  const handleFileNotarize = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const hash = await readFileHash(file);
    setHashInput(hash);
    await onNotarizeHash(hash);
  };

  const handleVerifyFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const hash = await readFileHash(file);
    setHashInput(hash);
    const result = await onVerifyHash(hash);
    setVerifyResult(result);
  };

  const handleVerifyHashInput = async () => {
    const result = await onVerifyHash(hashInput);
    setVerifyResult(result);
  };

  return (
    <section className="panel-card" aria-labelledby="poe-panel-title">
      <div className="panel-header">
        <h2 id="poe-panel-title">Proof Of Existence</h2>
      </div>

      <div className="tab-row">
        <AppButton
          type="button"
          variant="ghost"
          className={activeTab === 'notarize' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('notarize')}
        >
          Notarise Document
        </AppButton>
        <AppButton
          type="button"
          variant="ghost"
          className={activeTab === 'verify' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('verify')}
        >
          Verify Document
        </AppButton>
      </div>

      {activeTab === 'notarize' ? (
        <div className="poe-content">
          <input type="file" onChange={handleFileNotarize} aria-label="Upload file to notarise" />
          <input
            value={hashInput}
            onChange={(event) => setHashInput(event.target.value)}
            placeholder="Or paste bytes32 hash"
            aria-label="Hash for notarization"
            className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors"
          />
          <AppButton
            type="button"
            onClick={() => onNotarizeHash(hashInput)}
            variant="primary"
            loading={notarizing}
          >
            {notarizing ? 'Notarising...' : 'Notarise Hash'}
          </AppButton>
        </div>
      ) : (
        <div className="poe-content">
          <input type="file" onChange={handleVerifyFile} aria-label="Upload file to verify" className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors" />
          <input
            value={hashInput}
            onChange={(event) => setHashInput(event.target.value)}
            placeholder="Paste bytes32 hash"
            aria-label="Hash for verification"
            className="w-full px-3 py-2 rounded-card-sm bg-[#FAFAF7] border border-black/[0.06] text-[13px] text-lb-ink placeholder:text-lb-ghost focus:outline-none focus:border-lb-green-accent transition-colors"
          />
          <button type="button" onClick={handleVerifyHashInput} className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none">
            Verify Hash
          </button>
          {verifyResult && (
            <div className="verify-result" role="status">
              {verifyResult.exists ? (
                <p>
                  Verified on block #{verifyResult.blockNumber} at{' '}
                  {new Date(verifyResult.timestamp * 1000).toLocaleString()}
                </p>
              ) : (
                <p>Hash not found on-chain.</p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
